/**
 * withTimeout — race a promise against a deadline.
 *
 * WHY THIS EXISTS. Firestore runs memory-only here (no `persistentLocalCache`
 * — see `src/firebase.js` for the iPad history behind that). A consequence
 * that keeps surprising people: when the network drops or a congested link
 * stalls mid-flight, `setDoc` / `getDoc` do not reject. The SDK simply keeps
 * the write pending forever, waiting for a connection that may never come
 * back before the page is reloaded. Any `await` on such a call is a permanent
 * hang: a queue whose `_flushing` flag never clears, a save button stuck on
 * "Saving…", a clock sync that never releases its in-flight lock.
 *
 * So every write on a path a human is waiting behind gets a deadline, and a
 * hang is converted into an ordinary rejection the caller can handle.
 *
 * The timeout does NOT cancel the underlying operation — Firestore may still
 * commit it later. That is fine, and it is why the writes behind this helper
 * are idempotent (deterministic document ids, `setDoc(..., {merge: true})`):
 * a late commit and a retry converge on the same document state.
 */
export function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      reject(new Error(`${label || "firestore write"} timed out after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => { if (done) return; done = true; clearTimeout(timer); resolve(v); },
      (e) => { if (done) return; done = true; clearTimeout(timer); reject(e); },
    );
  });
}

/** Default deadline for a single user-facing Firestore write. */
export const WRITE_TIMEOUT_MS = 10000;
